import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { Grid, Box, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import CustomTextField from 'components/Forms/TextField';
import SubmitErrorMessage from 'components/Forms/SubmitErrorMessage';
import Loader from 'components/Loader';
import { generateErrorObject, resolveApiErrorMessage, isEditSaveDisabled } from 'utils';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import configErrors from './configErrors';
import validate from './validation';

const useStyles = makeStyles(theme => ({
  formHeader: {
    textAlign: 'center',
    marginBottom: '50px',
    fontSize: '18px',
    textTransform: 'capitalize',
    color: theme.palette.text.grey,
  },
  btnContainer: {
    marginTop: '100px',
  },
  btn: {
    minWidth: '140px',
  },
}));

const TaskForm = ({
  formType,
  onSubmitForm,
  submitting: formSubmitting,
  initialValues,
  backUrl,
  isReadOnly = false,
  onCancel,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const { t } = useTranslation();
  const isModalOpen = useSelector(state => state.modal.isOpen);
  const isEditMode = formType === 'edit';
  const [formData, setFormData] = useState(() => ({ name: '' }));
  const isInitializedRef = useRef(false);
  const initialFormDataRef = useRef(null);

  useEffect(() => {
    if (isEditMode && initialValues && !isInitializedRef.current) {
      const newFormData = {
        name: initialValues.name || '',
      };
      setFormData(newFormData);
      initialFormDataRef.current = newFormData;
      isInitializedRef.current = true;
    }
  }, [initialValues, isEditMode]);

  const handleSubmitForm = async values => {
    const transformedFormData = {
      name: values.name.trim(),
    };

    const submitData = await onSubmitForm(transformedFormData);
    if (submitData && submitData.error && submitData.message) {
      const mappedErrors = generateErrorObject(submitData.message, configErrors);
      if (Object.keys(mappedErrors).length > 0) {
        return mappedErrors;
      }

      return {
        [FORM_ERROR]: resolveApiErrorMessage(submitData.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      };
    }

    return undefined;
  };

  const isLoading = formSubmitting && !isModalOpen;
  const isFormReady = formType === 'create' || (isEditMode && (initialValues || isInitializedRef.current));

  if (!isFormReady) {
    return <Loader />;
  }

  return (
    <>
      <h2 className={classes.formHeader}>{formData.name}</h2>
      <Form
        key={isEditMode && initialFormDataRef.current ? `edit-${initialFormDataRef.current.name || 'new'}` : 'create'}
        onSubmit={handleSubmitForm}
        initialValues={initialFormDataRef.current || formData}
        validate={validate}
        render={({ handleSubmit, submitting, pristine, submitError, submitFailed }) => {
          return (
            <form onSubmit={handleSubmit}>
              <Grid container className="mainSection">
                <Grid item xs={4} />
                <Grid container item justify="flex-start" xs={4} direction="column">
                  <Grid item>
                    <Box>
                      <Field
                        type="text"
                        id="name"
                        name="name"
                        label={t('formLabels.jobTaskName')}
                        disabled={submitting || isReadOnly}
                        variant="outlined"
                        fullWidth
                        component={CustomTextField}
                        format={(value = '') => value.trim()}
                        formatOnBlur
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid container spacing={3} className={classes.btnContainer}>
                {!isReadOnly && (
                  <Grid item xs={6}>
                    <Box textAlign="right">
                      <Button
                        className={classes.btn}
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={isEditSaveDisabled({
                          submitting,
                          pristine,
                          isEditMode,
                        })}
                        size="large"
                      >
                        {t('buttons.save')}
                      </Button>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={isReadOnly ? 12 : 6}>
                  <Box textAlign={isReadOnly ? 'center' : 'left'}>
                    <Button
                      className={classes.btn}
                      variant="outlined"
                      size="large"
                      type="reset"
                      disabled={submitting}
                      onClick={() => {
                        if (onCancel) {
                          onCancel();
                          return;
                        }
                        history.push(backUrl);
                      }}
                    >
                      {t('buttons.cancel')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {submitFailed && submitError && <SubmitErrorMessage message={submitError} />}
              {isLoading && <Loader />}
            </form>
          );
        }}
      />
    </>
  );
};

TaskForm.defaultProps = {
  formType: 'create',
  initialValues: null,
  isReadOnly: false,
  onCancel: null,
};

TaskForm.propTypes = {
  formType: PropTypes.string,
  onSubmitForm: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  initialValues: PropTypes.shape({}),
  backUrl: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  onCancel: PropTypes.func,
};

export default TaskForm;
